import { Button, Divider, Flex, Popover, Spin, Typography } from 'antd'
import { useParams } from "react-router-dom"
import { useGetMoneyFlow } from "../api/case/document.ts"
import {
    useTraceMutation,
} from "../api/tracing/tracing.ts"
import { useMoneyflowVersionsModal } from '../modals/MoneyflowVersionsModal/useMoneyflowVersionsModal.ts'
import { useTracingStatuses } from "../hooks/useTracingStatuses.ts"
import { useCurrentStep } from "../hooks/useCurrentStep.ts"
import { TraceDirection } from "../backend/types/moneyflow.ts"
import { useGetSankeyData } from "../api/sankeyData/get.ts"
import { useGetMoneyflowVersions } from "../api/moneyflow/moneyflow.ts"
import { TaskStatus } from "../backend/types/task.ts"
import { InfoCircleOutlined, LinkOutlined } from '@ant-design/icons'

const MONEYFLOW_LIGHT_DEPTH_LIMIT = 10

export const MoneyFlowControls = () => {
    const { documentId } = useParams()
    const { mutate: trace, isPending: isMakingTraceRequest } = useTraceMutation(documentId!)
    const { currentStep } = useCurrentStep()
    const { data: moneyFlow, isFetching } = useGetMoneyFlow(documentId)
    const { data: versions, isFetching: versionsFetching } = useGetMoneyflowVersions(documentId!)
    const { data: sankeyData } = useGetSankeyData(moneyFlow?.sankeyData[currentStep])
    const { In, Out } = useTracingStatuses()
    const { show: showMoneyFlowVersions } = useMoneyflowVersionsModal()

    const {
        inboundDepth = 0, outboundDepth = 0
    } = sankeyData ?? {}
    const {
        blocked: moneyflowIsBlocked, forceResolution
    } = moneyFlow || {}

    const reachedInboundLimit = inboundDepth >= MONEYFLOW_LIGHT_DEPTH_LIMIT
    const reachedOutboundLimit = outboundDepth >= MONEYFLOW_LIGHT_DEPTH_LIMIT

    const isResolving =
        forceResolution?.status === TaskStatus.Queued ||
        forceResolution?.status === TaskStatus.Running

    const traceOnce = (direction: TraceDirection) => {
        if (!moneyFlow) return
        trace({
            documentId: documentId!,
            tracingId: moneyFlow.content.tracingId,
            direction,
            name: `${moneyFlow!.name} - ${direction === 'in' ?
                "Inbound " + (inboundDepth + 1) :
                "Outbound " + (outboundDepth + 1)}`
        })
    }

    function handleTrace(direction: TraceDirection) {
        traceOnce(direction)
    }

    const MFVersion = versions ? versions.map((v) => v._id).indexOf(documentId!) + 1 : 0

    const renderPopoverContent = () => (
        <Flex vertical gap={8}>
            <Typography.Text>
                Maximum 3 Hops in Light Version
            </Typography.Text>
            <a href="https://bitquery.io/products/moneyflow" target='_blank'>
                Please upgrade to MoneyFlow Pro <LinkOutlined/>
            </a>
        </Flex>
    )

    return (
        <Flex gap={8} align="center" wrap="wrap" style={{ flexGrow: 10 }}>
            <Button
                disabled={isFetching}
                size="small"
                type="primary"
                ghost
                onClick={() => showMoneyFlowVersions()}
            >

                V{MFVersion}.{currentStep}
                <Divider type="vertical"/>
                <Typography.Text>
                    In:{versionsFetching ?
                        <Spin spinning={versionsFetching}/> :
                    versions![MFVersion - 1].steps[currentStep].inboundDepth}
                    {' '} –
                    Out:{versionsFetching ?
                        <Spin spinning={versionsFetching}/> :
                    versions![MFVersion - 1].steps[currentStep].outboundDepth}
                </Typography.Text>

            </Button>

            <Flex align={"center"} gap={4} style={{ marginLeft: 'auto' }}>
                <Typography.Text strong>Tracing:</Typography.Text>
                <Popover title={reachedInboundLimit ? renderPopoverContent() : null}>
                    <Button
                        style={{ width: 100 }}
                        type="primary"
                        ghost
                        loading={In.isTracingRunning || isMakingTraceRequest}
                        disabled={In.isTracingRunning || reachedInboundLimit || moneyflowIsBlocked || isResolving}
                        size="small"
                        onClick={() => handleTrace('in')}>
                        Trace In
                        {reachedInboundLimit && <InfoCircleOutlined />}
                    </Button>
                </Popover>
                <Divider type="vertical"/>
                <Popover title={reachedOutboundLimit ? renderPopoverContent() : null}>
                    <Button
                        style={{ width: 100 }}
                        type="primary"
                        ghost
                        loading={Out.isTracingRunning || isMakingTraceRequest}
                        disabled={Out.isTracingRunning || reachedOutboundLimit || moneyflowIsBlocked || isResolving}
                        size="small"
                        onClick={() => handleTrace('out')}>
                        Trace Out
                        {reachedOutboundLimit && <InfoCircleOutlined />}
                    </Button>
                </Popover>
            </Flex>
        </Flex>
    )
}
